/**
 * **編集のための層**（Issue #523。`canvas/layers.js` に宣言だけしてあった `edit`）。
 *
 * 「**画面上でクリックしたらエディットモードになってスライダーで調節**」（ユーザー 2026-09-24）の
 * 「クリックして選ぶ」ところ。**GUI ライブラリの仕事ではない**（どんなパネルを使っても自前）ので、
 * 器が持つ。**書き出しには出さない**（`edit` 層なので）。
 *
 * ## 箱は器が既に持っているものから出す
 *
 * テーマに新しい口を足さない —— HUD は `anchors`（`ANCHOR_PORT`）、オブジェクトは
 * `objectParts` の板、盤は `view` の輪郭とゾーンの枠。**テーマを足しても付いてくる**。
 *
 * ## 押したときの選び方
 *
 * **一番小さい箱を優先**（`partAt`）。枠の上に LP が乗っているので、素直に「一番上の箱」だと
 * 枠しか掴めない。盤の輪郭はいちばん大きいので、何も無いところを押したときだけ当たる。
 */
import { useMemo } from 'react';
import { ANCHOR_PORT } from '../core/hud';
import { OBJECT_KINDS } from '../core/theme';
import { objectItems } from './ObjectLayer';
import './EditLayer.css';

/** 4 点 → 外接する箱（当たり判定は軸に沿った矩形でよい） */
function quadBox(quad) {
  if (!Array.isArray(quad) || quad.length !== 4) return null;
  const xs = quad.map((p) => p?.[0]); const ys = quad.map((p) => p?.[1]);
  if (![...xs, ...ys].every(Number.isFinite)) return null;
  const x = Math.min(...xs); const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

const ok = (b) => b && b.w > 0 && b.h > 0;
/** `hand` は x, y が中心（`core/hud` の契約） */
const centered = (b) => (b ? { ...b, x: b.x - b.w / 2, y: b.y - b.h / 2 } : null);

/**
 * **押せるものの一覧**（純関数）。`part` は `theme.schema` の `part` と同じ綴り
 * （宣言と選択がこれで繋がる）。
 */
export function editBoxes({ theme, scene, view, canvas, options, style, hudValues }) {
  const out = [];
  const push = (part, box, label) => { if (ok(box)) out.push({ part, label, ...box }); };

  // HUD（テーマが名前で答える箱）
  const anchors = theme?.hud?.anchors?.({ canvas, view, options, style });
  if (anchors && scene?.hud) {
    ANCHOR_PORT.forEach((name) => {
      const f = anchors[name];
      if (typeof f !== 'function') return;
      if (name === 'turnBadge') { push('hud:turnBadge', centered({ ...f(hudValues), y: 0 }), 'ターンの紋章'); return; }
      [0, 1].forEach((p) => {
        const b = f(p);
        push(`hud:${name}`, name === 'hand' ? centered(b) : b, `${name}（P${p + 1}）`);
      });
    });
  }

  // 盤に置かれているもの（ゾーンの枠を舞台のピクセルへ）
  const items = objectItems(scene?.board, scene);
  OBJECT_KINDS.forEach((kind) => {
    items.filter((i) => i.kind === kind.name).forEach((item) => {
      if (kind.space === 'screen') return;   // 実写の手札・盤の縁は下で拾う
      push(`objects:${kind.name}`, quadBox(view?.zoneQuad?.(item.zone)), item.zone);
    });
  });

  // 盤そのもの（いちばん大きいので、何も無いところを押したときに当たる）。
  // ゾーンの枠（`field:lines`）は板と同じ場所なので箱は作らない ——
  // GUI のフォルダには出るので、そちらから選ぶ
  push('field:matBack', quadBox(view?.outline), '盤');
  return out;
}

/** **一番小さい箱を優先**（枠の上の LP を掴めるように）。当たらなければ null */
export function partAt(point, boxes) {
  const hit = (boxes ?? []).filter((b) => (
    point.x >= b.x && point.x <= b.x + b.w && point.y >= b.y && point.y <= b.y + b.h
  ));
  if (!hit.length) return null;
  return hit.reduce((a, b) => ((a.w * a.h) <= (b.w * b.h) ? a : b));
}

/**
 * @param boxes `editBoxes()` の結果
 * @param selected 選ばれている `part`
 * @param scale 舞台の縮尺（`canvas/Canvas.js` が測ったもの。押した座標を舞台のピクセルへ戻す）
 * @param onSelect 押されたときに呼ぶ（`part` か null）
 */
export default function EditLayer({ boxes = [], selected = null, scale = 1, onSelect }) {
  const marks = useMemo(() => boxes.filter((b) => b.part === selected), [boxes, selected]);
  return (
    <div
      className="de-layer de-edit"
      data-layer="edit"
      data-selected={selected ?? ''}
      onPointerDown={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const k = scale > 0 ? scale : 1;
        const point = { x: (e.clientX - r.left) / k, y: (e.clientY - r.top) / k };
        onSelect?.(partAt(point, boxes)?.part ?? null);
      }}
    >
      {/* 選んでいるものを光らせる（同じ `part` の箱はぜんぶ ＝ P1 と P2 の両方） */}
      {marks.map((b, i) => (
        <div
          key={`${b.part}:${i}`}
          className="de-edit__mark"
          style={{ left: b.x, top: b.y, width: b.w, height: b.h }}
          data-testid={`edit-mark-${b.part}`}
        />
      ))}
      {selected ? <div className="de-edit__name" data-testid="edit-name">{selected}</div> : null}
    </div>
  );
}
